import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { debounce } from 'lodash';
import { FaRegCheckCircle } from 'react-icons/fa';
import styled from 'styled-components';

import { useScreenQuery, Modal, Button, TextInput, ErrorMessage } from 'playup-web-core';

import { ResetPasswordForm } from '../../../@types/ResetPasswordForm';
import { updatePassword } from '../../../api/users';
import { parseApiError, parseApiErrors } from '../../../utils/parseApiError';
import useNotify from '../../../hooks/useNotify';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import usePasswordCheck from '../../../hooks/usePasswordCheck';

const UpdatePasswordModal = ({ onClose }: Props) => {
	const notify = useNotify();
	const { isSmall } = useScreenQuery();
	const { refreshPortalUser } = React.useContext(PortalSessionContext);
	const methods = useForm<ResetPasswordForm>({});
	const { register, handleSubmit } = methods;

	const [sending, setSending] = React.useState(false);
	const [errors, setErrors] = React.useState<APIError>();

	const [password, setPassword] = React.useState<string>('');
	const { includeUpperAndLowerCase, includeAtLeastOneNumber, hasEightOrMoreCharacters } = usePasswordCheck(password);

	// why are we not using ASYNC functions here?
	const onSubmit = (data: ResetPasswordForm) => {
		setSending(true);

		updatePassword({ ...data })
			.then(() => {
				refreshPortalUser();
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your password has been updated!',
					timeout: 2000,
				});
			})
			.catch((error) => {
				setErrors(error.errors);
			})
			.finally(() => {
				setSending(false);
				onClose();
			});
	};

	const onPasswordChange = debounce((e) => {
		setPassword(e.target.value);
	}, 300);

	return (
		<Modal title="Reset Password" onClose={onClose}>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<StyledTextInput
						kind="white"
						label="Current Password"
						type="password"
						placeholder="Current Password"
						error={parseApiError(errors, 'password')}
						{...register('password')}
					/>
					<StyledTextInput
						kind="white"
						label="New Password"
						type="password"
						placeholder="New Password"
						error={parseApiError(errors, 'new_password')}
						{...register('new_password')}
						onChange={onPasswordChange}
					/>
					<PasswordRequirementsSection>
						<PasswordRequirement isSmallScreen={isSmall}>
							<CheckIcon checked={includeUpperAndLowerCase} />
							Includes lower and upper case
						</PasswordRequirement>
						<PasswordRequirement isSmallScreen={isSmall}>
							<CheckIcon checked={includeAtLeastOneNumber} />
							Includes at least one number
						</PasswordRequirement>
						<PasswordRequirement isSmallScreen={isSmall}>
							<CheckIcon checked={hasEightOrMoreCharacters} />
							Password must be 8 or more characters
						</PasswordRequirement>
					</PasswordRequirementsSection>
					<StyledTextInput
						kind="white"
						label="Confirm New Password"
						type="password"
						placeholder="Confirm New Password"
						error={parseApiError(errors, 'new_confirm_password')}
						{...register('new_confirm_password')}
					/>
					<StyledButton kind="white" type="submit" isLoading={sending} height={50}>
						Update Password
					</StyledButton>
					{errors && errors.error && (
						<ErrorMessage>{parseApiErrors(errors, ['password', 'new_password', 'new_confirm_password'])}</ErrorMessage>
					)}
				</form>
			</FormProvider>
		</Modal>
	);
};

export default UpdatePasswordModal;

interface Props {
	onClose: () => void;
}

const StyledTextInput = styled(TextInput)({
	marginBottom: '20px',
});

const StyledButton = styled(Button)({
	margin: '30px 0',
});

const PasswordRequirementsSection = styled.section({
	marginLeft: '10px',
	marginBottom: '20px',
});

const PasswordRequirement = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	marginTop: isSmallScreen ? '5px' : '10px',
	marginBottom: isSmallScreen ? '5px' : '10px',
	color: theme.colors.darkR,
	fontSize: isSmallScreen ? '14px' : '18px',
}));

const CheckIcon = styled(FaRegCheckCircle)<{ checked: boolean }>(({ theme, checked }) => ({
	display: 'inline-block',
	marginRight: '5px',
	color: checked ? theme.colors.success : theme.colors.greyR,
}));
